import {
  type TerminalEntry,
  TerminalEntryKind,
  type TerminalFolder,
  TerminalInputKind,
  type TerminalLinkMode,
  TerminalNoticeType,
  type TerminalOutputChunk,
  type TerminalServerMessage,
  TerminalServerMessageType,
  TerminalSessionStatus,
  type TerminalSessionSummary,
} from '@this+that/models';

import type {
  TerminalPendingInput,
  TerminalSessionState,
  TerminalState,
} from './types';
import { partitionAttachments } from '../../components/Terminal/utils/terminalAttachments';
import { TerminalPendingStatus } from '../../enums/terminal';
import {
  TERMINAL_HISTORY_MAX_ENTRIES,
  TERMINAL_MOVE_HIGHLIGHT_MS,
  TERMINAL_SHELL_PREFIX,
} from '../../utils/constants/terminalConstants';

export function appendOutputChunks(
  output: TerminalOutputChunk[],
  appended: TerminalOutputChunk[],
): TerminalOutputChunk[] {
  const merged = [...output];
  for (const chunk of appended) {
    const last = merged[merged.length - 1];
    if (last && last.stream === chunk.stream) {
      merged[merged.length - 1] = { ...last, text: last.text + chunk.text };
    } else {
      merged.push({ ...chunk });
    }
  }
  return merged;
}

export function mergeEntry(
  existing: TerminalEntry | undefined,
  incoming: TerminalEntry,
): TerminalEntry | null {
  if (incoming.kind !== TerminalEntryKind.SHELL_APPEND) {
    return incoming;
  }
  if (existing?.kind !== TerminalEntryKind.SHELL) {
    return null;
  }
  return {
    ...existing,
    output: appendOutputChunks(existing.output, incoming.appendOutput),
    exitCode: incoming.exitCode ?? existing.exitCode,
    running: incoming.running ?? existing.running,
  };
}

function findInsertIndex(session: TerminalSessionState, seq: number): number {
  let index = session.entries.length;
  while (index > 0) {
    const previous = session.entries[index - 1];
    if ((session.orderSeqById[previous.id] ?? 0) <= seq) {
      break;
    }
    index -= 1;
  }
  return index;
}

export function upsertEntry(
  session: TerminalSessionState,
  seq: number,
  incoming: TerminalEntry,
): TerminalSessionState {
  const appliedSeq = session.appliedSeqById[incoming.id];
  if (appliedSeq !== undefined && seq <= appliedSeq) {
    return session;
  }
  const existingIndex = session.entries.findIndex(
    (entry) => entry.id === incoming.id,
  );
  const merged = mergeEntry(session.entries[existingIndex], incoming);
  if (!merged) {
    return session;
  }
  const lastSeq = Math.max(session.lastSeq, seq);
  const appliedSeqById = { ...session.appliedSeqById, [incoming.id]: seq };
  if (existingIndex >= 0) {
    const entries = [...session.entries];
    entries[existingIndex] = merged;
    return { ...session, entries, appliedSeqById, lastSeq };
  }
  const entries = [...session.entries];
  entries.splice(findInsertIndex(session, seq), 0, merged);
  return {
    ...session,
    entries,
    appliedSeqById,
    orderSeqById: { ...session.orderSeqById, [incoming.id]: seq },
    lastSeq,
  };
}

export function createSessionState(
  summary: TerminalSessionSummary,
  entries: TerminalEntry[],
): TerminalSessionState {
  const firstSeq = summary.lastSeq - entries.length + 1;
  const seqById: Record<string, number> = {};
  entries.forEach((entry, index) => {
    seqById[entry.id] = firstSeq + index;
  });
  return {
    summary,
    entries,
    orderSeqById: seqById,
    appliedSeqById: { ...seqById },
    lastSeq: summary.lastSeq,
    clearedBeforeSeq: 0,
    history: [],
    pendingInputs: [],
    draftAttachments: [],
    attachmentError: null,
    reorderBuffer: {},
  };
}

function applySnapshot(
  state: TerminalState,
  message: Extract<
    TerminalServerMessage,
    { type: TerminalServerMessageType.SESSION_SNAPSHOT }
  >,
): Partial<TerminalState> {
  const { session, entries } = message;
  const known = state.sessions[session.id];
  if (known) {
    const isOlder =
      session.statusSeq !== undefined &&
      known.summary.statusSeq !== undefined &&
      session.statusSeq < known.summary.statusSeq;
    if (isOlder) {
      return {};
    }
    return {
      isLoadingSessions: false,
      sessions: {
        ...state.sessions,
        [session.id]: { ...known, summary: session },
      },
    };
  }
  const shouldActivate = state.isAwaitingOpen || state.activeSessionId === null;
  return {
    sessions: {
      ...state.sessions,
      [session.id]: createSessionState(session, entries),
    },
    sessionOrder: [...state.sessionOrder, session.id],
    isAwaitingOpen: false,
    isLoadingSessions: false,
    activeSessionId: shouldActivate ? session.id : state.activeSessionId,
    focusNonce: shouldActivate ? state.focusNonce + 1 : state.focusNonce,
  };
}

function matchesPending(
  pending: TerminalPendingInput,
  entry: TerminalEntry,
): boolean {
  if (pending.kind === TerminalInputKind.SHELL) {
    return (
      (entry.kind === TerminalEntryKind.SHELL &&
        entry.command === pending.text) ||
      (entry.kind === TerminalEntryKind.PROMPT &&
        entry.text === `${TERMINAL_SHELL_PREFIX}${pending.text}`)
    );
  }
  return entry.kind === TerminalEntryKind.PROMPT && entry.text === pending.text;
}

export function reconcilePending(
  session: TerminalSessionState,
  entry: TerminalEntry,
): TerminalSessionState {
  const index = session.pendingInputs.findIndex((pending) =>
    matchesPending(pending, entry),
  );
  if (index < 0) {
    return session;
  }
  const pendingInputs = session.pendingInputs.filter((_, i) => i !== index);
  return { ...session, pendingInputs };
}

function freezeLinkNotices(
  session: TerminalSessionState,
  taskId: string | undefined,
  linkMode: TerminalLinkMode | undefined,
): TerminalSessionState {
  if (!taskId || !linkMode) {
    return session;
  }
  let changed = false;
  const entries = session.entries.map((entry) => {
    const isOpenLinkNotice =
      entry.kind === TerminalEntryKind.NOTICE &&
      entry.taskId === taskId &&
      entry.linkMode === undefined &&
      (entry.type === TerminalNoticeType.MOVED ||
        entry.type === TerminalNoticeType.ATTACHED);
    if (!isOpenLinkNotice) {
      return entry;
    }
    changed = true;
    return { ...entry, linkMode };
  });
  return changed ? { ...session, entries } : session;
}

function applyOne(
  session: TerminalSessionState,
  seq: number,
  entry: TerminalEntry,
): TerminalSessionState {
  const isNew = !session.entries.some((existing) => existing.id === entry.id);
  const frozen =
    isNew && entry.kind === TerminalEntryKind.NOTICE
      ? freezeLinkNotices(
          session,
          session.summary.linkedTaskId,
          session.summary.linkMode,
        )
      : session;
  const upserted = upsertEntry(frozen, seq, entry);
  const reconciled = isNew ? reconcilePending(upserted, entry) : upserted;
  return { ...reconciled, lastSeq: seq };
}

function drainBuffer(session: TerminalSessionState): TerminalSessionState {
  let current = session;
  let next = current.reorderBuffer[current.lastSeq + 1];
  while (next) {
    const { [current.lastSeq + 1]: _consumed, ...rest } = current.reorderBuffer;
    current = applyOne(
      { ...current, reorderBuffer: rest },
      current.lastSeq + 1,
      next,
    );
    next = current.reorderBuffer[current.lastSeq + 1];
  }
  return current;
}

export function sequenceEntry(
  session: TerminalSessionState,
  seq: number,
  entry: TerminalEntry,
): TerminalSessionState {
  if (seq <= session.lastSeq) {
    return session;
  }
  if (seq === session.lastSeq + 1) {
    return drainBuffer(applyOne(session, seq, entry));
  }
  return {
    ...session,
    reorderBuffer: { ...session.reorderBuffer, [seq]: entry },
  };
}

export function flushBuffer(
  session: TerminalSessionState,
): TerminalSessionState {
  let current = session;
  const seqs = Object.keys(session.reorderBuffer)
    .map(Number)
    .sort((a, b) => a - b);
  for (const seq of seqs) {
    const { [seq]: entry, ...rest } = current.reorderBuffer;
    current = applyOne({ ...current, reorderBuffer: rest }, seq, entry);
  }
  return current;
}

function applyEntryMessage(
  state: TerminalState,
  message: Extract<
    TerminalServerMessage,
    { type: TerminalServerMessageType.SESSION_ENTRY }
  >,
): Partial<TerminalState> {
  const session = state.sessions[message.sessionId];
  if (!session) {
    return {};
  }
  const updated = sequenceEntry(session, message.seq, message.entry);
  if (updated === session) {
    return {};
  }
  return { sessions: { ...state.sessions, [message.sessionId]: updated } };
}

export function flushSessionBuffer(
  state: TerminalState,
  sessionId: string,
): Partial<TerminalState> {
  const session = state.sessions[sessionId];
  if (!session || Object.keys(session.reorderBuffer).length === 0) {
    return {};
  }
  return {
    sessions: { ...state.sessions, [sessionId]: flushBuffer(session) },
  };
}

function isStale(applied: number | undefined, incoming: number | undefined) {
  return applied !== undefined && incoming !== undefined && incoming <= applied;
}

function isStaleStatus(
  applied: number | undefined,
  incoming: number | undefined,
): boolean {
  return isStale(applied, incoming);
}

function applyStatusMessage(
  state: TerminalState,
  message: Extract<
    TerminalServerMessage,
    { type: TerminalServerMessageType.SESSION_STATUS }
  >,
): Partial<TerminalState> {
  const session = state.sessions[message.sessionId];
  if (!session || isStaleStatus(session.summary.statusSeq, message.statusSeq)) {
    return {};
  }
  const previous = session.summary;
  const isRelinked =
    previous.linkedTaskId !== undefined &&
    (previous.linkedTaskId !== message.linkedTaskId ||
      previous.linkMode !== message.linkMode);
  const settled = isRelinked
    ? freezeLinkNotices(session, previous.linkedTaskId, previous.linkMode)
    : session;
  const summary: TerminalSessionSummary = {
    ...session.summary,
    statusSeq: message.statusSeq ?? session.summary.statusSeq,
    status: message.status,
    cwd: message.cwd,
    branch: message.branch,
    permissionMode: message.permissionMode,
    model: message.model,
    claudeModel: message.claudeModel,
    additionalDirs: message.additionalDirs,
    linkedTaskId: message.linkedTaskId,
    linkMode: message.linkMode,
    activeRunId: message.activeRunId,
    linkedDelegationId: message.linkedDelegationId,
    adoptedJobId: message.adoptedJobId,
  };
  return {
    sessions: {
      ...state.sessions,
      [message.sessionId]: { ...settled, summary },
    },
  };
}

function applyClosedMessage(
  state: TerminalState,
  sessionId: string,
): Partial<TerminalState> {
  const session = state.sessions[sessionId];
  if (!session) {
    return {};
  }
  const summary = { ...session.summary, status: TerminalSessionStatus.ENDED };
  return {
    sessions: { ...state.sessions, [sessionId]: { ...session, summary } },
  };
}

function applyPickedFolder(
  state: TerminalState,
  folder: TerminalFolder,
): Partial<TerminalState> {
  const isKnown = state.folders.some((item) => item.path === folder.path);
  return {
    isPickingFolder: false,
    folders: isKnown ? state.folders : [...state.folders, folder],
  };
}

function applyFolders(folders: TerminalFolder[]): Partial<TerminalState> {
  return { folders };
}

export function applyServerMessageToState(
  state: TerminalState,
  message: TerminalServerMessage,
): Partial<TerminalState> {
  switch (message.type) {
    case TerminalServerMessageType.SESSION_SNAPSHOT:
      return applySnapshot(state, message);
    case TerminalServerMessageType.SESSION_ENTRY:
      return applyEntryMessage(state, message);
    case TerminalServerMessageType.SESSION_STATUS:
      return applyStatusMessage(state, message);
    case TerminalServerMessageType.SESSION_CLOSED:
      return applyClosedMessage(state, message.sessionId);
    case TerminalServerMessageType.FOLDERS:
      return applyFolders(message.folders);
    case TerminalServerMessageType.FOLDERS_PICKED:
      return applyPickedFolder(state, message.folder);
    default:
      return {};
  }
}

export function removeSessionFromState(
  state: TerminalState,
  sessionId: string,
): Partial<TerminalState> {
  if (!(sessionId in state.sessions)) {
    return {};
  }
  const { [sessionId]: _removed, ...sessions } = state.sessions;
  const sessionOrder = state.sessionOrder.filter((id) => id !== sessionId);
  const activeSessionId =
    state.activeSessionId === sessionId
      ? (sessionOrder[sessionOrder.length - 1] ?? null)
      : state.activeSessionId;
  return { sessions, sessionOrder, activeSessionId };
}

export function clearSessionOutput(
  state: TerminalState,
  sessionId: string,
): Partial<TerminalState> {
  const session = state.sessions[sessionId];
  if (!session) {
    return {};
  }
  return {
    sessions: {
      ...state.sessions,
      [sessionId]: { ...session, clearedBeforeSeq: session.lastSeq },
    },
  };
}

export function appendHistory(
  state: TerminalState,
  sessionId: string,
  text: string,
): Partial<TerminalState> {
  const session = state.sessions[sessionId];
  if (!session || session.history[session.history.length - 1] === text) {
    return {};
  }
  const history = [...session.history, text].slice(
    -TERMINAL_HISTORY_MAX_ENTRIES,
  );
  return {
    sessions: { ...state.sessions, [sessionId]: { ...session, history } },
  };
}

export function renameSessionInState(
  state: TerminalState,
  sessionId: string,
  name: string,
): Partial<TerminalState> {
  const session = state.sessions[sessionId];
  if (!session) {
    return {};
  }
  const customName = name.trim() || undefined;
  return {
    sessions: { ...state.sessions, [sessionId]: { ...session, customName } },
  };
}

export function markSuggestionDismissed(
  state: TerminalState,
  sessionId: string,
  entryId: string,
): Partial<TerminalState> {
  const session = state.sessions[sessionId];
  if (!session) {
    return {};
  }
  const entries = session.entries.map((entry) =>
    entry.id === entryId && entry.kind === TerminalEntryKind.SUGGESTION
      ? { ...entry, dismissed: true }
      : entry,
  );
  return {
    sessions: { ...state.sessions, [sessionId]: { ...session, entries } },
  };
}

export function addPending(
  state: TerminalState,
  sessionId: string,
  input: TerminalPendingInput,
): Partial<TerminalState> {
  const session = state.sessions[sessionId];
  if (!session) {
    return {};
  }
  const pendingInputs = [...session.pendingInputs, input];
  return {
    sessions: { ...state.sessions, [sessionId]: { ...session, pendingInputs } },
  };
}

export function setPending(
  state: TerminalState,
  sessionId: string,
  localId: string,
  status: TerminalPendingStatus,
): Partial<TerminalState> {
  const session = state.sessions[sessionId];
  if (!session) {
    return {};
  }
  const pendingInputs = session.pendingInputs.map((pending) =>
    pending.localId === localId ? { ...pending, status } : pending,
  );
  return {
    sessions: { ...state.sessions, [sessionId]: { ...session, pendingInputs } },
  };
}

export function removePending(
  state: TerminalState,
  sessionId: string,
  localId: string,
): Partial<TerminalState> {
  const session = state.sessions[sessionId];
  if (!session) {
    return {};
  }
  const pendingInputs = session.pendingInputs.filter(
    (pending) => pending.localId !== localId,
  );
  return {
    sessions: { ...state.sessions, [sessionId]: { ...session, pendingInputs } },
  };
}

let draftCounter = 0;

export function addDrafts(
  state: TerminalState,
  sessionId: string,
  files: File[],
): Partial<TerminalState> {
  const session = state.sessions[sessionId];
  if (!session) {
    return {};
  }
  const { accepted, error } = partitionAttachments(
    files,
    session.draftAttachments.length,
  );
  const added = accepted.map((file) => {
    draftCounter += 1;
    return { id: `draft-${draftCounter}`, file };
  });
  return {
    sessions: {
      ...state.sessions,
      [sessionId]: {
        ...session,
        draftAttachments: [...session.draftAttachments, ...added],
        attachmentError: error,
      },
    },
  };
}

export function removeDraft(
  state: TerminalState,
  sessionId: string,
  id: string,
): Partial<TerminalState> {
  const session = state.sessions[sessionId];
  if (!session) {
    return {};
  }
  return {
    sessions: {
      ...state.sessions,
      [sessionId]: {
        ...session,
        draftAttachments: session.draftAttachments.filter(
          (draft) => draft.id !== id,
        ),
        attachmentError: null,
      },
    },
  };
}

export function clearDrafts(
  state: TerminalState,
  sessionId: string,
): Partial<TerminalState> {
  const session = state.sessions[sessionId];
  if (!session) {
    return {};
  }
  return {
    sessions: {
      ...state.sessions,
      [sessionId]: { ...session, draftAttachments: [], attachmentError: null },
    },
  };
}

export function setProgress(
  state: TerminalState,
  sessionId: string,
  localId: string,
  progress: number,
): Partial<TerminalState> {
  const session = state.sessions[sessionId];
  if (!session) {
    return {};
  }
  const pendingInputs = session.pendingInputs.map((pending) =>
    pending.localId === localId ? { ...pending, progress } : pending,
  );
  return {
    sessions: { ...state.sessions, [sessionId]: { ...session, pendingInputs } },
  };
}

let highlightTimer: ReturnType<typeof setTimeout> | undefined;

export function cancelHighlightTimer(): void {
  clearTimeout(highlightTimer);
  highlightTimer = undefined;
}

export function flashHighlight(
  setHighlight: (taskId: string | null) => void,
  taskId: string,
): void {
  cancelHighlightTimer();
  setHighlight(taskId);
  highlightTimer = setTimeout(() => {
    highlightTimer = undefined;
    setHighlight(null);
  }, TERMINAL_MOVE_HIGHLIGHT_MS);
}
